/**
 * Utilitaires pour le formatage et le traitement de markdown
 */

/**
 * Fonction utilitaire pour formater correctement le contenu markdown
 * Assure que les titres, listes et paragraphes sont correctement espacés
 */
export const formatMarkdown = (content: string): string => {
	// Assurons-nous d'avoir des sauts de ligne avant et après les titres
	let result = content;

	// Prétraitement: S'assurer qu'il y a un saut de ligne au début si le texte commence par un titre
	if (result.match(/^#{1,6}\s+/)) {
		result = '\n' + result;
	}

	// 1. Corriger les titres (#, ##, etc.) pour qu'ils aient des lignes vides avant et après
	result = result
		// Assurer un saut de ligne avant chaque titre s'il n'y en a pas déjà un
		.replace(/([^\n])\n(#{1,6}\s+)/g, '$1\n\n$2')
		// Assurer un saut de ligne après chaque titre
		.replace(/(#{1,6}\s+[^\n]+)(\n[^#\n])/g, '$1\n\n$2')
		// Pour les titres en fin de texte, assurer un saut de ligne
		.replace(/(#{1,6}\s+[^\n]+)$/, '$1\n\n');

	// 2. Corriger les listes pour qu'elles soient correctement formatées
	result = result
		// Ajouter un saut de ligne avant chaque liste (s'il n'y en a pas déjà)
		.replace(/([^\n])\n(\s*[-*+]\s)/g, '$1\n\n$2')
		.replace(/([^\n])\n(\s*\d+\.\s)/g, '$1\n\n$2');

	// 3. S'assurer que les paragraphes sont bien séparés
	result = result.replace(/([^\n])\n([^\s#\-*+\d])/g, '$1\n\n$2');

	// 4. Normaliser les fins de ligne
	result = result
		.replace(/\n{3,}/g, '\n\n') // Remplacer 3+ sauts de ligne par exactement 2
		.trim(); // Éliminer les espaces au début et à la fin

	// 5. Corriger spécifiquement la syntaxe des titres pour s'assurer qu'il y a un espace après les #
	result = result.replace(/(#{1,6})([^#\s])/g, '$1 $2');

	return result;
};

/**
 * Fonction utilitaire pour nettoyer le HTML des attributs node et mieux structurer les titres
 */
export const cleanNodeAttributes = (html: string): string => {
	// Vérifier si le contenu est une chaîne de caractères
	if (typeof html !== 'string') {
		console.warn(
			'cleanNodeAttributes: contenu non textuel reçu',
			typeof html
		);
		return String(html || '');
	}

	// Supprimer tous les attributs node="[object Object]" et autres attributs node
	let cleaned = html
		.replace(/\s+node="[^"]*"/g, '')
		.replace(/\s+node='[^']*'/g, '')
		.replace(/\s+node=[^\s>]*/g, '');

	// Supprimer également data-sourcepos qui peut contenir des informations inutiles
	cleaned = cleaned.replace(/\s+data-sourcepos="[^"]*"/g, '');

	// Détecter et corriger les faux titres H3 qui sont en fait des listes numérotées
	// Par exemple: "<h3>2. <strong>Titre</strong> - contenu...</h3>"
	cleaned = cleaned.replace(
		/<h3>(\d+\.\s+)(.+?)<\/h3>/g,
		(match, number, content) => {
			return `<p>${number}${content}</p>`;
		}
	);

	return cleaned;
};

/**
 * Fonction utilitaire pour nettoyer le HTML et préserver le markdown et les balises d'action
 */
export const cleanHtmlForMarkdown = (html: string): string => {
	// Nettoyer d'abord les attributs node et les faux titres
	const cleanedHtml = cleanNodeAttributes(html);

	// Préserver les balises d'action avec une regex plus robuste
	// qui capte également les attributs complexes
	let result = cleanedHtml.replace(
		/\[([\w]+(?:\s+[\w]+=(?:"[^"]*"|'[^']*'|\S+))*)\]/g,
		(match) => {
			return `___ACTION_PLACEHOLDER___${match}___ACTION_PLACEHOLDER___`;
		}
	);

	// Remplacer les balises HTML courantes par leur équivalent markdown
	result = result
		// Nettoyer les espaces et retours à la ligne superflus mais préserver les doubles retours à la ligne
		.replace(/\s+/g, (match) => {
			return match.includes('\n\n') ? '\n\n' : ' ';
		})
		// Paragraphes
		.replace(/<p.*?>(.*?)<\/p>/g, '$1\n\n')
		// En-têtes
		.replace(/<h1.*?>(.*?)<\/h1>/g, '# $1\n\n')
		.replace(/<h2.*?>(.*?)<\/h2>/g, '## $1\n\n')
		.replace(/<h3.*?>(.*?)<\/h3>/g, '### $1\n\n')
		.replace(/<h4.*?>(.*?)<\/h4>/g, '#### $1\n\n')
		.replace(/<h5.*?>(.*?)<\/h5>/g, '##### $1\n\n')
		.replace(/<h6.*?>(.*?)<\/h6>/g, '###### $1\n\n')
		// Mise en forme
		.replace(/<strong.*?>(.*?)<\/strong>/g, '**$1**')
		.replace(/<b.*?>(.*?)<\/b>/g, '**$1**')
		.replace(/<em.*?>(.*?)<\/em>/g, '*$1*')
		.replace(/<i.*?>(.*?)<\/i>/g, '*$1*')
		// Liens
		.replace(/<a.*?href="(.*?)".*?>(.*?)<\/a>/g, '[$2]($1)')
		// Listes
		.replace(/<ul.*?>(.*?)<\/ul>/g, (match, content) => {
			return content.replace(/<li.*?>(.*?)<\/li>/g, '- $1\n');
		})
		.replace(/<ol.*?>(.*?)<\/ol>/g, (match, content) => {
			let index = 1;
			return content.replace(/<li.*?>(.*?)<\/li>/g, () => {
				return `${index++}. $1\n`;
			});
		})
		// Images
		.replace(/<img.*?src="(.*?)".*?alt="(.*?)".*?>/g, '![$2]($1)')
		.replace(/<img.*?alt="(.*?)".*?src="(.*?)".*?>/g, '![$1]($2)')
		.replace(/<img.*?src="(.*?)".*?>/g, '![]($1)')
		// Balises <br> en retours à la ligne
		.replace(/<br\s*\/?>/g, '\n')
		// Retirer les autres balises HTML
		.replace(/<[^>]*>/g, '');

	// Restaurer les balises d'action
	result = result.replace(
		/___ACTION_PLACEHOLDER___(.*?)___ACTION_PLACEHOLDER___/g,
		'$1'
	);

	// Formater le markdown obtenu pour s'assurer qu'il est bien structuré
	return formatMarkdown(result);
};
